import { gql } from '@apollo/client';
import {
  NewOrder_VisibleVariantPackQuery,
  NewOrder_VisibleVariantPackQueryVariables,
} from '@/types';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineVisibleVariantPackAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { print as printQuery } from 'graphql';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

const VISIBLE_VARIANT_PACKS_QUERY = gql`
  query NewOrder_VisibleVariantPack(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    visible_variant_packs(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const useDownloadVisibleVariantPacks = () => {
  const locale = useItemQueryLocale();
  const getVisibleVariantPacks = useLazyQuery<
    NewOrder_VisibleVariantPackQuery,
    NewOrder_VisibleVariantPackQueryVariables
  >(VISIBLE_VARIANT_PACKS_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const downloadVisibleVariantPacks = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    const variables = {
      locale,
      restaurantUUID,
      skipGroupMemberships: true,
      skipLiveSalt: true,
      skipSaltApi: true,
      supplierUUID,
      useSaltCache: false,
    };

    const { data: visibleVariantPacksData } = await getVisibleVariantPacks({
      context: {
        offlineSync: true,
      },
      variables,
    });

    const adapter = new OfflineVisibleVariantPackAdapter(
      restaurantUUID,
      supplierUUID,
    );
    await adapter.save(visibleVariantPacksData);

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(VISIBLE_VARIANT_PACKS_QUERY),
        syncType: OfflineSyncType.VisibleVariantPacks,
        variables,
      },
    );
  };

  return { downloadVisibleVariantPacks };
};
